import { client } from './client';
import { BadgeSource, type BadgeAggregateCounts, type UserBadge } from '../../models/Badge';
import { type MinimalUser } from '../../models/User';
import { toMinimalUser } from './profileMappers';
import { throwIfSupabaseError } from './throwIfSupabaseError';

const table = 'user_badges';

export async function getBadgesForUser(userId: string): Promise<UserBadge[]> {
    const response = await client.rpc('get_user_badges', { p_user_id: userId });
    throwIfSupabaseError(response);

    return (response.data ?? []).map(toUserBadge);
}

export async function getActivityCountsForUser(userId: string): Promise<BadgeAggregateCounts> {
    const response = await client.rpc('get_user_activity_counts', { p_user_id: userId });
    throwIfSupabaseError(response);

    // Each new count column ships behind its migration; toNumber() defaults
    // missing columns to 0 so the UI stays safe to deploy ahead of the SQL.
    const row = response.data?.[0] as Record<string, unknown> | undefined;
    return {
        playerCount: toNumber(row?.player_count),
        gmCount: toNumber(row?.gm_count),
        systemsPlayedCount: toNumber(row?.systems_played_count),
        systemsGmedCount: toNumber(row?.systems_gmed_count),
        maxRepeatedGmedCount: toNumber(row?.max_repeated_gmed_count),
        currentStreak: toNumber(row?.current_streak),
        longestStreak: toNumber(row?.longest_streak),
        mentorsCount: toNumber(row?.distinct_gms_played_count),
        uniquePlayersGmedCount: toNumber(row?.unique_players_gmed_count),
    };
}

function toNumber(value: unknown): number {
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'string' && value.length > 0) {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

export type AwardBadgeInput = {
    userId: string;
    badgeKey: string;
    notes?: string;
};

export async function awardBadge(input: AwardBadgeInput): Promise<UserBadge> {
    const authResponse = await client.auth.getUser();
    throwIfSupabaseError(authResponse);

    const awardedById = authResponse.data.user?.id ?? null;

    const response = await client
        .from(table)
        .insert({
            user_id: input.userId,
            badge_key: input.badgeKey,
            source: BadgeSource.Manual,
            notes: input.notes ?? null,
            awarded_by: awardedById,
        })
        .select('id, badge_key, source, awarded_at, awarded_by, notes')
        .single();
    throwIfSupabaseError(response);

    const data = response.data;
    return {
        id: data.id,
        key: data.badge_key,
        source: data.source === BadgeSource.Manual ? BadgeSource.Manual : BadgeSource.Auto,
        awardedAt: new Date(data.awarded_at),
        awardedByUsername: undefined,
        notes: data.notes ?? undefined,
    };
}

export type BadgeHolder = MinimalUser & {
    /** Earliest matching award among the requested keys. Drives display order. */
    awardedAt: Date;
    /** The matching key the holder earned first — useful when callers pass
     *  a tier set ({bronze, silver, gold}) and want to show each user's
     *  earliest qualifying tier. */
    badgeKey: string;
};

/**
 * Fetches holders for any user_badge whose key matches one of `badgeKeys`.
 * Pass a single key for a special badge, or a full tier set for a future
 * "bronze-or-higher" leaderboard. Empty input short-circuits without an RPC.
 */
export async function getUsersWithAnyBadge(badgeKeys: string[]): Promise<BadgeHolder[]> {
    if (badgeKeys.length === 0) {
        return [];
    }

    const response = await client.rpc('get_users_with_any_badge', { p_badge_keys: badgeKeys });
    throwIfSupabaseError(response);

    return (response.data ?? []).map((row) => ({
        ...toMinimalUser({
            user_id: row.user_id,
            username: row.username,
            image_url: row.image_url,
            discord_username: row.discord_username,
            role: row.role,
        }),
        awardedAt: new Date(row.awarded_at),
        badgeKey: row.badge_key,
    }));
}

export async function removeBadge(badgeId: string): Promise<void> {
    const response = await client
        .from(table)
        .delete()
        .eq('id', badgeId);
    throwIfSupabaseError(response);
}

type RawBadgeRow = {
    id: string;
    badge_key: string;
    source: string;
    awarded_at: string;
    awarded_by_username?: string | null;
    notes?: string | null;
};

function toUserBadge(row: RawBadgeRow): UserBadge {
    return {
        id: row.id,
        key: row.badge_key,
        source: row.source === BadgeSource.Manual ? BadgeSource.Manual : BadgeSource.Auto,
        awardedAt: new Date(row.awarded_at),
        awardedByUsername: row.awarded_by_username ?? undefined,
        notes: row.notes ?? undefined,
    };
}
